/**
 * Chapter 5, the competitors: the comparison (08), what we learn from each
 * (33), the whitespace map (34) and the open space by the numbers (55). Each
 * competitor's snapshot is template 32 (brandSlides.tsx).
 */
import type { StrategyCompetitors } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Headline, Picture, Slide, SourceLine, V,
  accountName, dayLong, slideText,
  type TemplateContext,
} from "./kit";

/** The colour an account takes in the set's charts: the brand blue, then the set's own. */
const SET_COLOURS = ["#121212", "#6a00ba", "#04e735", "#9a9a9a", "#f66666", "#ff9f1c", "#00a3a3", "#c2185b", "#795548", "#607d8b"];
export function accountColour(competitors: StrategyCompetitors | undefined, account: string): string {
  if (account === "brand") return "#207cf5";
  const i = (competitors?.set ?? []).findIndex((c) => c.id === account);
  return SET_COLOURS[Math.max(0, i) % SET_COLOURS.length];
}

/* ── 08 Comparison ────────────────────────────────────────────────────── */

export function ComparisonSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const set = doc.competitors!.set;
  const indexes = ctx.slide.template.id === "comparison" ? ctx.slide.template.competitors : [];
  const t = slideText(ctx);
  const brandScore = doc.brand?.scorecard?.total_shown;
  const readAt = set.find((c) => c.scorecard?.read_at)?.scorecard?.read_at;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Who holds *the attention.*" size={56} lineHeight={63} />
        <div className="sv-compare">
          {indexes.map((i) => {
            const c = set[i];
            const s = c.scorecard;
            const parts = s?.presence && s.content && s.community ? `Presence ${Math.round(s.presence.points)} · Content ${Math.round(s.content.points)} · Community ${Math.round(s.community.points)}.` : null;
            return (
              <div key={c.id} className="sv-compare-card">
                <Picture ctx={ctx} slot={`competitor:${c.id}`} label={`${c.name}’s picture`} url={c.picture_url} className="sv-compare-pic" />
                <div className="sv-compare-head">
                  <V as="p" className="sv-compare-name">{c.name}</V>
                  <span className="sv-push" />
                  {s?.total_shown != null ? <p className="sv-compare-score"><V as="b">{Math.round(s.total_shown)}</V><span> / 100</span></p> : null}
                </div>
                {c.kind_line ? <V as="p" className="sv-compare-kind">{c.kind_line}</V> : null}
                <p className="sv-compare-text">{c.summary_line ? <V>{c.summary_line} </V> : null}{parts ? <V>{parts}</V> : null}</p>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} flag={<SourceLine className="sv-flag-line">Scores out of 100{readAt ? <>, public read <V>{dayLong(readAt)}</V></> : null}{brandScore != null ? <> · <V>{ctx.brand} {Math.round(brandScore)}</V></> : null}</SourceLine>} />
      </div>
    </Slide>
  );
}

/* ── 33 What we learn from each ───────────────────────────────────────── */

export function LessonsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const competitors = doc.competitors!;
  const rows = ctx.slide.template.id === "lessons" ? ctx.slide.template.rows : [];
  const t = slideText(ctx);
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="What we learn *from each.*" size={54} lineHeight={60} />
        {t.reading ? <V as="p" className="sv-reading-lg sv-reading-14">{t.reading}</V> : null}
        <span className="sv-push" />
        <div className="sv-table sv-table-ink sv-table-20">
          <div className="sv-table-row sv-table-head">
            <p />
            <p>They do well</p>
            <p>They leave room</p>
            <p>Our angle</p>
          </div>
          {rows.map((i) => {
            const l = competitors.lessons[i];
            return (
              <div key={l.competitor_id} className="sv-table-row">
                <V as="p" className="sv-table-key">{accountName(doc, ctx.brand, l.competitor_id)}</V>
                <V as="p">{l.does_well}</V>
                <V as="p">{l.leaves_room}</V>
                <V as="p">{l.our_angle ?? ""}</V>
              </div>
            );
          })}
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 34 The whitespace map ────────────────────────────────────────────── */

/** The plot: x from −1 (left) to 1 (right), y from −1 (bottom) to 1 (top). */
const PLOT = { left: 120, right: 1032, top: 20, middle: 210, bottom: 380 };
const px = (x: number) => PLOT.left + ((x + 1) / 2) * (PLOT.right - PLOT.left);
const py = (y: number) => (y >= 0 ? PLOT.middle - y * (PLOT.middle - PLOT.top) : PLOT.middle - y * (PLOT.bottom - PLOT.middle));

export function MapSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const map = doc.competitors!.whitespace_map!;
  const t = slideText(ctx);
  const box = map.open_region?.box;
  const today = map.brand_today;
  const next = map.brand_next;
  return (
    <Slide>
      <div className="sv-col sv-fill sv-map-copy">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The *whitespace.*" size={50} lineHeight={56} />
        <div className="sv-map">
          <i className="sv-map-axis-x" style={{ left: PLOT.left, width: PLOT.right - PLOT.left, top: PLOT.middle }} />
          <i className="sv-map-axis-y" style={{ left: (PLOT.left + PLOT.right) / 2, top: PLOT.top, height: PLOT.bottom - PLOT.top }} />
          <V as="p" className="sv-map-label" style={{ left: PLOT.left, top: PLOT.middle + 4 }}>{map.axes.x.low}</V>
          <V as="p" className="sv-map-label sv-map-label-end" style={{ right: 1172 - PLOT.right, top: PLOT.middle + 4 }}>{map.axes.x.high}</V>
          <V as="p" className="sv-map-label sv-map-label-centre" style={{ left: (PLOT.left + PLOT.right) / 2, top: 0 }}>{map.axes.y.high}</V>
          <V as="p" className="sv-map-label sv-map-label-centre" style={{ left: (PLOT.left + PLOT.right) / 2, top: PLOT.bottom + 4 }}>{map.axes.y.low}</V>
          {box && map.open_region ? (
            <div className="sv-map-region" style={{ left: px(box.x0), top: py(box.y1), width: px(box.x1) - px(box.x0), height: py(box.y0) - py(box.y1) }}>
              <V as="p" className="sv-map-region-key">{map.open_region.label}</V>
              {map.open_region.line ? <V as="p" className="sv-map-region-line">{map.open_region.line}</V> : null}
            </div>
          ) : null}
          {today && next ? (
            <svg className="sv-map-move" width="1172" height="400" aria-hidden>
              <defs>
                <marker id="sv-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                  <path d="M1 1 9 5 1 9" fill="none" stroke="#207cf5" strokeWidth="1.5" />
                </marker>
              </defs>
              <line x1={px(next.x) - 10} y1={py(next.y)} x2={px(today.x) + 10} y2={py(today.y)} stroke="#207cf5" strokeWidth="1.5" strokeDasharray="5 4" markerStart="url(#sv-arrow)" markerEnd="url(#sv-arrow)" />
            </svg>
          ) : null}
          {map.placements.map((p) => (
            <div key={p.account} className="sv-map-dot" style={{ left: px(p.x), top: py(p.y) }}>
              <i />
              <V>{accountName(doc, ctx.brand, p.account)}</V>
            </div>
          ))}
          {today ? <div className="sv-map-dot sv-map-today" style={{ left: px(today.x), top: py(today.y) }}><i /><span><V>{ctx.brand}</V> today</span></div> : null}
          {next ? <div className="sv-map-dot sv-map-next" style={{ left: px(next.x), top: py(next.y) }}><i /><span><V>{ctx.brand}</V>, {ctx.periodEnd?.slice(0, 4) ?? "next"}</span></div> : null}
        </div>
        {t.reading ? <V as="p" className="sv-map-reading">{t.reading}</V> : null}
        <Foot ctx={ctx} label="The whitespace" flag={<SourceLine className="sv-flag-line">Placed by <V>{map.placed_by ?? "the strategist"}</V>{map.read_at ? <> from the read of <V>{dayLong(map.read_at)}</V></> : null} · axes judged</SourceLine>} />
      </div>
    </Slide>
  );
}

/* ── 55 The open space by the numbers ─────────────────────────────────── */

const TRACK = { width: 700, perPct: 10 };

function verdict(shares: Array<{ account: string; share_pct: number }>) {
  const sorted = [...shares].sort((a, b) => b.share_pct - a.share_pct);
  const [top, second] = sorted;
  if (!top || top.share_pct <= 10) return { kind: "open" as const, top };
  if (top.share_pct >= 40 && (second?.share_pct ?? 0) <= 20) return { kind: "owned" as const, top };
  return { kind: "crowded" as const, top };
}

export function OpenSpaceSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const competitors = doc.competitors!;
  const t = slideText(ctx);
  const accounts = ["brand", ...competitors.set.filter((c) => c.status !== "dormant").map((c) => c.id)];
  const window = competitors.mix_summary?.sample;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="The open space, *by the numbers.*" />
        <div className="sv-space">
          <div className="sv-space-legend">
            <p className="sv-key">Share of each account’s posts, 90 days</p>
            {accounts.map((a) => (
              <span key={a}><i style={{ background: accountColour(competitors, a) }} /><V>{accountName(doc, ctx.brand, a)}</V></span>
            ))}
          </div>
          <div className="sv-space-scale">
            {[0, 10, 20, 40, 60].map((v) => <span key={v} style={{ left: 260 + 8 + v * TRACK.perPct }}>{v}%</span>)}
          </div>
          {competitors.territories.map((row) => {
            const v = verdict(row.shares);
            const ordered = [...row.shares].sort((a, b) => (a.account === "brand" ? 1 : b.account === "brand" ? -1 : 0));
            return (
              <div key={row.id} className="sv-space-row" data-open={v.kind === "open" || undefined}>
                <V as="p" className="sv-space-topic">{row.territory}</V>
                <div className="sv-space-track">
                  <i className="sv-space-line" />
                  <i className="sv-space-ten" style={{ left: 8 + 10 * TRACK.perPct }} />
                  {ordered.map((s) => (
                    <i key={s.account} className="sv-space-dot" data-brand={s.account === "brand" || undefined} title={`${accountName(doc, ctx.brand, s.account)}: ${s.share_pct}%`} style={{ left: 8 + Math.min(70, s.share_pct) * TRACK.perPct - 6, background: accountColour(competitors, s.account) }} />
                  ))}
                </div>
                <p className="sv-space-verdict">
                  {v.kind === "open" ? <>OPEN · highest <V>{v.top?.share_pct ?? 0}%</V></>
                    : v.kind === "owned" ? <>OWNED · <V>{accountName(doc, ctx.brand, v.top!.account)} {v.top!.share_pct}%</V></>
                    : <>CROWDED · led by <V>{accountName(doc, ctx.brand, v.top!.account)} {v.top!.share_pct}%</V></>}
                </p>
              </div>
            );
          })}
          {t.reading ? <V as="p" className="sv-space-reading">{t.reading}</V> : null}
          <SourceLine className="sv-source-plain">
            Posts of the {accounts.length} accounts{window?.window_start ? <>, <V>{dayLong(window.window_start)} to {dayLong(window.window_end)}</V></> : null}, sorted by topic. A topic is open when no account gives it more than 10% of its posts; owned when one account gives it 40% or more and nobody else passes 20%.
          </SourceLine>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
